import React, { createContext, useState, useContext, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useHistory } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { getEventParticipationSubmissionState } from "../utilities/eventParticipationSubmissionState";

const EventParticipationSubmissionContext = createContext();

export const useEventParticipationSubmission = () => {
    const context = useContext(EventParticipationSubmissionContext);
    if (!context) {
        throw new Error("useEventParticipationSubmission must be used within an EventParticipationSubmissionProvider");
    }
    return context;
};

export function EventParticipationSubmissionProvider({ children, eventId, event: initialEvent, onEventChange }) {
    const [formData, setFormData] = useState({
        address: "",
        postalCode: "",
        city: "",
        country: "",
        phoneNumber: "",
        manufacturer: "",
        model: "",
        buildYear: "",
        licensePlate: "",
        horsePower: "",

        vehicleImageId: null,
        vehicleImageUrl: null,
        vehicleImage: null,

        customFields: {},
        acceptConditions: false,
    });

    const [errors, setErrors] = useState({});
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasPendingSubmission, setHasPendingSubmission] = useState(null);
    const [hasAcceptedSubmission, setHasAcceptedSubmission] = useState(null);
    const [event, setEvent] = useState(initialEvent ?? null);
    const history = useHistory();

    useEffect(() => {
        setEvent(initialEvent ?? null);
    }, [initialEvent]);

    const fetchEvent = useCallback(async () => {
        setErrors(prev => ({ ...prev, event: null }));
        try {
            const response = await axios.post("/getEvent", { eventId });
            setEvent(response.data.event);
            onEventChange?.(response.data.event);
        } catch (requestError) {
            console.error("Error fetching event:", requestError);
            setErrors(prev => ({ ...prev, event: "Er is een fout opgetreden bij het laden van het evenement. Probeer het later opnieuw." }));
        }
    }, [eventId, onEventChange]);

    useEffect(() => {
        fetchEvent();
    }, [fetchEvent]);

    const checkUserEventParticipationSubmissions = useCallback(async () => {
        try {
            const response = await axios.post("/checkUserEventParticipationSubmissions", { eventId });
            const isPending = response.data.isPending;
            const isAccepted = response.data.isAccepted;

            setHasPendingSubmission(isPending);
            setHasAcceptedSubmission(isAccepted);

            if (isPending || isAccepted) {
                history.replace(`/event/${eventId}/participation-submission`);
            }
        } catch (requestError) {
            console.error("Error fetching existing submissions:", requestError);
        } finally {
            setLoading(false);
        }
    }, [eventId, history]);

    useEffect(() => {
        checkUserEventParticipationSubmissions();
    }, [checkUserEventParticipationSubmissions]);

    const fetchCustomFields = useCallback(async () => {
        setFields([]);
        setLoading(true);
        try {
            const response = await axios.post("/getEventParticipationFields", { eventId });
            setFields(response.data.fields);
        } catch (requestError) {
            console.error("Error fetching custom fields:", requestError);
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => {
        fetchCustomFields();
    }, [fetchCustomFields]);

    const updateFormData = useCallback((field, value) => {
        setFormData(prevData => ({
            ...prevData,
            [field]: value
        }));
    }, []);

    const updateCustomField = useCallback((fieldId, value) => {
        setFormData(prevData => ({
            ...prevData,
            customFields: {
                ...prevData.customFields,
                [fieldId]: value
            }
        }));
    }, []);

    const disclaimerUrl = event?.disclaimerUrl ?? event?.organisation?.disclaimerUrl;
    const privacyStatementUrl = event?.privacyStatementUrl ?? event?.organisation?.privacyStatementUrl;
    const termsAndConditionsUrl = event?.termsAndConditionsUrl ?? event?.organisation?.termsAndConditionsUrl;

    const hasLegalUrls = disclaimerUrl || privacyStatementUrl || termsAndConditionsUrl;
    const {
        enabled: participationSubmissionsEnabled,
        open: participationSubmissionsOpen,
    } = getEventParticipationSubmissionState(event);

    const submitForm = useCallback(async () => {
        setErrors(prev => ({ ...prev, submit: null }));

        try {
            const formattedData = {
                eventId,
                ...formData,
                customFields: formData.customFields || {},
                ...(hasLegalUrls ? { acceptConditions: formData.acceptConditions } : { acceptConditions: true })
            };

            await axios.post("/submitEventParticipation", formattedData);

            setFormData({
                address: "",
                postalCode: "",
                city: "",
                country: "",
                phoneNumber: "",
                manufacturer: "",
                model: "",
                buildYear: "",
                licensePlate: "",
                horsePower: "",
                customFields: {},
                acceptConditions: false,
            });

            return true;
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error submitting form:", error);

            const errorCode = error?.response?.data?.error ?? "SERVER_ERROR";

            if (errorCode === "PARTICIPATION_SUBMISSION_CLOSED") {
                setEvent(prevEvent => prevEvent ? ({
                    ...prevEvent,
                    eventParticipationSubmissionsOpen: false,
                }) : prevEvent);
                await fetchEvent();
                setErrors(prev => ({
                    ...prev,
                    submit: "De inschrijvingen voor dit evenement zijn gesloten.",
                }));
                history.replace(`/event/${eventId}/participation-submission`);
                return false;
            }

            if (errorCode === "PARTICIPATION_SUBMISSION_NOT_ENABLED") {
                setEvent(prevEvent => prevEvent ? ({
                    ...prevEvent,
                    eventParticipationSubmissionsEnabled: false,
                    eventParticipationSubmissionsOpen: false,
                }) : prevEvent);
                await fetchEvent();
                history.replace(`/event/${eventId}`);
                return false;
            }

            setErrors(prev => ({
                ...prev,
                submit: `Er ging iets mis bij het indienen van het formulier. Probeer het later opnieuw. (${errorCode})`,
            }));
            return false;
        }
    }, [eventId, fetchEvent, formData, hasLegalUrls, history]);

    const value = {
        event,
        formData,
        errors,
        updateFormData,
        updateCustomField,
        submitForm,
        customFields : fields,
        loading,
        hasPendingSubmission,
        hasAcceptedSubmission,
        hasLegalUrls,
        participationSubmissionsEnabled,
        participationSubmissionsOpen,
        refreshEvent: fetchEvent,
    };

    return (
        <EventParticipationSubmissionContext.Provider value={value}>
            {children}
        </EventParticipationSubmissionContext.Provider>
    );
}

EventParticipationSubmissionProvider.propTypes = {
    children: PropTypes.node.isRequired,
    eventId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]).isRequired,
    event: PropTypes.object,
    onEventChange: PropTypes.func,
};

export default EventParticipationSubmissionProvider;
